import { useContext } from "react";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { AuthContext, AuthProvider } from "../features/auth/context/AuthContext"; // Import AuthContext
import RouteScrollToTop from "./RouteScrollToTop";
import PhosphorIconInit from "./PhosphorIconInit";
import HomePage from "../features/home/pages/HomePage";
import OrderTrack from "../features/orders/pages/OrderTrack";
import ProductDetailsLegacy from "../features/products/pages/ProductDetailsLegacy";
import ProductDetailsPage from "../features/products/pages/ProductDetailsPage";
import VendorPage from "../features/vendors/pages/VendorPage";
import CartPage from "../features/cart/pages/CartPage";
import Blog from "../features/content/pages/Blog";
import BlogDetails from "../features/content/pages/BlogDetails";
import GuestCheckoutPage from "../features/checkout/pages/GuestCheckoutPage";
import ProductsPage from "../features/products/pages/ProductsPage";
import LoginSignUpPage from "../features/auth/pages/LoginSignUpPage";
import WishlistPage from "../features/wishlist/pages/WishlistPage";
import ProtectedRoute from "./ProtectedRoute";
import PublicRoute from "./PublicRoute";
import ProductSearchPage from "../features/products/pages/ProductSearchPage";
import HotDealsPage from "../features/collections/pages/HotDealsPage";
import ReturnSuccessPage from "../features/checkout/pages/ReturnSuccessPage";
import ReturnCancelPage from "../features/checkout/pages/ReturnCancelPage";

import ContactUsPage from "../features/content/pages/ContactUsPage";
import MyAccountPage from "../features/account/pages/MyAccountPage";
import AddressBookPage from "../features/account/pages/AddressBookPage";
import RegisteredCheckoutPage from "../features/checkout/pages/RegisteredCheckoutPage";
import MyOrdersPage from "../features/orders/pages/MyOrdersPage";
import OrderDetailsPage from "../features/orders/pages/OrderDetailsPage";
import TrackOrderPage from "../features/orders/pages/TrackOrderPage";
import CollectionPage from "../features/collections/pages/CollectionPage";
import PrivacyPolicyPage from "../features/content/pages/PrivacyPolicyPage";
import AboutUsPage from "../features/content/pages/AboutUsPage";
import TermsAndConditionsPage from "../features/content/pages/TermsAndConditionsPage";
import ReturnsAndRefundPolicyPage from "../features/content/pages/ReturnsAndRefundPolicyPage";
import OrderSuccessPage from "../features/checkout/pages/OrderSuccessPage";
import PaymentVerificationPage from "../features/checkout/pages/PaymentVerificationPage";
import GuestCouponPage from "../features/coupons/pages/GuestCouponPage";
import { HelmetProvider } from 'react-helmet-async';

import NotFound from "../features/content/pages/NotFound";

// Old category page links (breadcrumbs, shared URLs) open the filterable products page.
function CategoryProductsRedirect() {
  const { id } = useParams();
  return <Navigate to={`/products?category=${encodeURIComponent(id)}`} replace />;
}

function AppRoutes() {
  const { isAuthenticated } = useContext(AuthContext); // Access authentication state

  return (
    <Routes>

      <Route exact path="/" element={<HomePage />} />
      <Route exact path="/orders" element={<OrderTrack />} />
      <Route exact path="/product-details-one/:id" element={<ProductDetailsLegacy />} />
      <Route exact path="/product-details/:slug" element={<ProductDetailsPage />} />
      <Route exact path="/category-products/:id" element={<CategoryProductsRedirect />} />
      <Route exact path="/shop/:id" element={<VendorPage />} />
      <Route exact path="/cart" element={<CartPage />} />
      <Route exact path="/login" element={<PublicRoute><LoginSignUpPage /></PublicRoute>} />
      <Route exact path="/blog" element={<Blog />} />
      <Route exact path="/blog-details" element={<BlogDetails />} />
      <Route exact path="/contact" element={<ContactUsPage />} />
      <Route exact path="/track-order" element={<TrackOrderPage />} />
      <Route exact path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route exact path="/about-us" element={<AboutUsPage />} />
      <Route exact path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
      <Route exact path="/return-policy" element={<ReturnsAndRefundPolicyPage />} />
      <Route exact path="/order-success" element={<OrderSuccessPage />} />
      <Route exact path="/payment-verification" element={<PaymentVerificationPage />} />
      <Route exact path="/guest-coupon" element={<GuestCouponPage />} />

      {/* ✅ Serve Different Pages for Authenticated & Guest Users on /checkout */}
      <Route
        exact
        path="/checkout"
        element={isAuthenticated ? <RegisteredCheckoutPage /> : <GuestCheckoutPage />}
      />
      <Route exact path="/my-orders" element={<ProtectedRoute><MyOrdersPage /></ProtectedRoute>} />
      <Route exact path="/products" element={<ProductsPage />} />
      <Route exact path="/wishlist" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
      <Route exact path="/search" element={<ProductSearchPage />} />
      <Route exact path="/hotdeals" element={<HotDealsPage />} />
      <Route exact path="/account" element={<ProtectedRoute><MyAccountPage /></ProtectedRoute>} />
      <Route exact path="/address-book" element={<ProtectedRoute><AddressBookPage /></ProtectedRoute>} />
      <Route exact path="/orderdetails/:id" element={<ProtectedRoute><OrderDetailsPage /></ProtectedRoute>} />

      {/* Payment & Order Processing */}
      <Route exact path="/returnSuccess" element={<ReturnSuccessPage />} />
      <Route exact path="/returnCanceled" element={<ReturnCancelPage />} />
      
      {/* Any collection, linked as /<collection type>/<slug>; fixed routes above take priority. */}
      <Route path="/:collectionType/:slug" element={<CollectionPage />} />

      {/* Default Redirect for Unknown Routes */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <RouteScrollToTop />
        <PhosphorIconInit />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
